/**
 * Deterministic maths for the simulation (SPEC §3.6).
 *
 * Only + - * /, Math.sqrt, Math.abs, Math.min/max and Math.floor are used here, since
 * those are exactly specified by IEEE 754 / ECMAScript. Math.sin/cos/atan2 and friends can
 * differ between engines, so sin, cos and atan2 are implemented with fixed polynomials.
 */

export const PI = 3.141592653589793;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
const QUARTER_PI = 0.7853981633974483;

export function sqrt(x: number): number {
  return Math.sqrt(x);
}

/** Reduce an angle to [-PI, PI]. */
export function wrapAngle(a: number): number {
  return a - TWO_PI * Math.floor((a + PI) / TWO_PI);
}

/** sin on [-PI/2, PI/2]: odd Taylor series up to x^13 (error < 1e-9). */
function sinCore(x: number): number {
  const x2 = x * x;
  return (
    x *
    (1 +
      x2 *
        (-1 / 6 +
          x2 *
            (1 / 120 +
              x2 * (-1 / 5040 + x2 * (1 / 362880 + x2 * (-1 / 39916800 + x2 * (1 / 6227020800)))))))
  );
}

export function sin(x: number): number {
  let a = wrapAngle(x);
  // Fold [-PI, PI] into [-PI/2, PI/2] using sin(PI - a) = sin(a).
  if (a > HALF_PI) a = PI - a;
  else if (a < -HALF_PI) a = -PI - a;
  return sinCore(a);
}

export function cos(x: number): number {
  return sin(x + HALF_PI);
}

/** atan on [0, 1]: argument reduction plus a Taylor series. */
function atanCore(t: number): number {
  // Reduce with atan(t) = PI/4 + atan((t - 1) / (t + 1)) when t > tan(PI/8).
  let offset = 0;
  let x = t;
  if (x > 0.4142135623730951) {
    offset = QUARTER_PI;
    x = (x - 1) / (x + 1);
  }
  // |x| <= tan(PI/8) ~ 0.4142, so the alternating series converges quickly (error < 1e-9).
  const x2 = x * x;
  const series =
    x *
    (1 +
      x2 *
        (-1 / 3 +
          x2 *
            (1 / 5 +
              x2 *
                (-1 / 7 +
                  x2 *
                    (1 / 9 + x2 * (-1 / 11 + x2 * (1 / 13 + x2 * (-1 / 15 + x2 * (1 / 17)))))))));
  return offset + series;
}

export function atan(x: number): number {
  const a = Math.abs(x);
  let r: number;
  if (a <= 1) {
    r = atanCore(a);
  } else {
    r = HALF_PI - atanCore(1 / a);
  }
  return x < 0 ? -r : r;
}

/** Four-quadrant arctangent, matching Math.atan2 conventions for the cases the sim needs. */
export function atan2(y: number, x: number): number {
  if (x === 0 && y === 0) return 0;
  if (x === 0) return y > 0 ? HALF_PI : -HALF_PI;
  const a = atan(y / x);
  if (x > 0) return a;
  return y >= 0 ? a + PI : a - PI;
}

export const DEG = 0.017453292519943295;
export const RAD_TO_DEG = 57.29577951308232;

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Cubic smoothstep on t in [0, 1] (input is clamped). */
export function smoothstep(t: number): number {
  const c = clamp01(t);
  return c * c * (3 - 2 * c);
}

export function sign(x: number): number {
  return x < 0 ? -1 : 1;
}
